import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useSnackbar } from 'notistack'
import {
  useGetDefinitionQuery, useSetEnabledMutation, useDeactivateRecipientMutation, useReactivateRecipientMutation,
  usePreviewAlertMutation,
} from '../../redux/internalAlertsApiSlice'
import HealthBadge from './HealthBadge'
import ConfirmDialog from './ConfirmDialog'
import RecipientForm from './RecipientForm'
import EditDefinitionForm from './EditDefinitionForm'
import ChangeLogList from './ChangeLogList'
import RunResultDialog from './RunResultDialog'
import { cadenceLabel, channelMeta, channelOf, count, headerLabel, isLocked, recipientAddress, relTime, runSummary, shortTime } from './alertUtils'
import { BAR_BG, GREEN, INK, LINE, MUTED, PRIMARY, RED, WARNING, WARNING_LIGHT, Field, Pill, SCard, Th, btnSx } from './ui'

/**
 * One alert in full — and where it is controlled: switch it on/off, add or remove recipients, edit its details,
 * see who changed what. "Reached in last run" answers "is Karmpal on this alert, and is he getting it?".
 * Every write asks for confirmation; reasons go into the change log. Email alerts show each recipient's header slot
 * (To / CC / BCC) and the property the list replaced; the Preview / "last Proship run" block is WhatsApp-only.
 *
 * <p>Until an alert is migrated (sender flag flipped to Proship through the admin API) Control Tower still sends it
 * with its own list, so the dialog is read-only for it and says so. The cutover switch, Send now, Send test and the
 * snapshot operations are admin-API-only by decision — only Preview lives here.
 */
export default function AlertDetailDialog({ templateName, open, onClose, onShowHistory }) {
  const { enqueueSnackbar } = useSnackbar()
  const { data: d, isFetching, error } = useGetDefinitionQuery(templateName, { skip: !open || !templateName })
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(false)
  const [confirm, setConfirm] = useState(null)   // { kind: 'enable'|'disable'|'remove'|'readd', recipient? }
  useEffect(() => { if (!open) { setAdding(false); setEditing(false); setConfirm(null) } }, [open])

  const [setEnabled, { isLoading: toggling }] = useSetEnabledMutation()
  const [deactivate, { isLoading: removing }] = useDeactivateRecipientMutation()
  const [reactivate, { isLoading: readding }] = useReactivateRecipientMutation()
  const [preview, { isLoading: previewing }] = usePreviewAlertMutation()
  const [runResult, setRunResult] = useState(null)
  const busy = toggling || removing || readding || previewing
  const hasJob = Boolean(d?.jobKey)
  const locked = isLocked(d)
  const channel = channelOf(d)
  const email = channel === 'EMAIL'
  const wa = channel === 'WHATSAPP'
  const meta = channelMeta(channel)

  const fail = (err, fallback) => enqueueSnackbar(err?.data?.message || err?.message || fallback, { variant: err?.status === 409 ? 'warning' : 'error', autoHideDuration: 8000 })

  const onPreview = async () => {
    try {
      const res = await preview(templateName).unwrap()
      setRunResult({ title: 'Preview — what Proship would send now', result: res })
    } catch (err) {
      fail(err, 'Preview failed')
    }
  }

  const reached = new Set(d?.lastRun?.recipientsReached || [])
  const active = (d?.recipients || []).filter(r => r.active)
  const inactive = (d?.recipients || []).filter(r => !r.active)
  const hasRun = Boolean(d?.lastRun?.at)

  const doConfirm = async (reason) => {
    const c = confirm
    setConfirm(null)
    try {
      if (c.kind === 'enable' || c.kind === 'disable') {
        await setEnabled({ templateName, enabled: c.kind === 'enable', reason }).unwrap()
        enqueueSnackbar(c.kind === 'enable' ? 'Alert switched on — next run will send' : 'Alert switched off — next run will be skipped', { variant: 'success' })
      } else if (c.kind === 'remove') {
        await deactivate({ templateName, key: recipientAddress(c.recipient), reason }).unwrap()
        enqueueSnackbar(`${c.recipient.displayName || recipientAddress(c.recipient)} removed — takes effect on the next run`, { variant: 'success' })
      } else if (c.kind === 'readd') {
        await reactivate({ templateName, key: recipientAddress(c.recipient) }).unwrap()
        enqueueSnackbar(`${c.recipient.displayName || recipientAddress(c.recipient)} re-added`, { variant: 'success' })
      }
    } catch (err) {
      fail(err, 'Could not save the change')
    }
  }
  const others = (r) => active.filter(a => recipientAddress(a) !== recipientAddress(r))
  const healthTone = ['FAILING', 'MISSED'].includes(d?.health?.state) ? 'error' : ['PARTIAL', 'LATE'].includes(d?.health?.state) ? 'warning' : 'info'

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth='lg' fullWidth PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
      {/* ── Header band ── */}
      <Box sx={{ background: '#fff', borderBottom: `3px solid ${locked ? WARNING : PRIMARY}`, px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ width: 4, height: 40, borderRadius: 2, background: locked ? WARNING : PRIMARY, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: INK, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{d?.displayName || templateName}</Typography>
            {d && <HealthBadge state={d.health?.state} reason={d.health?.reason} />}
          </Box>
          <Typography sx={{ fontSize: '0.68rem', color: MUTED, fontFamily: 'monospace', mt: 0.4 }}>{templateName}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {d && (
            <Tooltip arrow title={locked ? 'Still sent by Control Tower — not editable here yet' : d.enabled ? 'Switch off: Proship skips this alert from its next run' : 'Switch on: Proship resumes on its next run'}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.4, borderRadius: '8px', background: BAR_BG, border: `1px solid ${LINE}` }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: d.enabled ? GREEN : MUTED }}>{d.enabled ? 'ON' : 'OFF'}</Typography>
                <Switch size='small' checked={Boolean(d.enabled)} disabled={busy || locked} onChange={(e) => setConfirm({ kind: e.target.checked ? 'enable' : 'disable' })} />
              </Box>
            </Tooltip>
          )}
          <IconButton size='small' onClick={onClose} aria-label='Close' disabled={busy}><CloseIcon fontSize='small' /></IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ background: BAR_BG, p: 2.5 }}>
        {error && <Alert severity='error' sx={{ mb: 2 }}>{error?.data?.message || error?.message || 'Could not load this alert.'}</Alert>}
        {isFetching && !d && <Box>{[...Array(6)].map((_, i) => <Skeleton key={i} height={32} sx={{ mb: 0.5 }} />)}</Box>}

        {d && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {locked && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1, borderRadius: '12px', background: WARNING_LIGHT, border: `1px solid ${WARNING}33` }}>
                <LockOutlinedIcon sx={{ fontSize: 18, color: WARNING }} />
                <Typography sx={{ fontSize: '0.8rem', color: INK, lineHeight: 1.45 }}>
                  <b>Still sent by Control Tower</b> — recipients and on/off are read-only here until this alert is migrated to Proship.
                </Typography>
              </Box>
            )}
            {d.health?.reason && !locked && (
              <Alert severity={healthTone} sx={{ py: 0.5, borderRadius: '12px', fontSize: '0.8rem' }}>
                {d.health.reason}{d.health.evaluatedAt && <Box component='span' sx={{ color: MUTED }}> · evaluated {shortTime(d.health.evaluatedAt)} IST</Box>}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2, alignItems: 'start' }}>
              {/* ── Overview ── */}
              <SCard title='Overview' sub={d.description || undefined}
                right={!locked && !editing && <Button size='small' startIcon={<EditOutlinedIcon />} onClick={() => setEditing(true)} disabled={busy} sx={btnSx}>Edit</Button>}>
                <Box sx={{ p: 2 }}>
                  {editing ? (
                    <EditDefinitionForm def={d} onDone={() => setEditing(false)} onCancel={() => setEditing(false)} />
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 2 }}>
                      <Field label='Schedule'>{cadenceLabel(d.expectedCadence)}</Field>
                      {wa && <Field label='Messages'>{d.messagesPerRun === 'MULTI' ? 'Several per recipient, 5 merchants each' : 'One per recipient'}</Field>}
                      <Field label='Owner'>{d.ownerEmail}</Field>
                      <Field label='Channel'>{meta.label}</Field>
                      {d.senderClass && <Field label='Sent by' mono>{d.senderClass}</Field>}
                      {d.legacyPropertyKey && (
                        <Field label='Replaced property' mono>
                          <Tooltip arrow title='The application property the recipients were read from before this panel existed. It only seeds the first list on a fresh database; after that this definition is the source of truth.'>
                            <Box component='span'>{d.legacyPropertyKey.split(',').join(', ')}</Box>
                          </Tooltip>
                        </Field>
                      )}
                    </Box>
                  )}
                </Box>
              </SCard>

              {/* ── Runs ── */}
              <SCard title='Runs' sub={wa ? 'last send seen in the delivery log, and what Proship computes' : d.tracked === false ? 'not tracked — this list is only read by other mails' : 'last send recorded by the sender'}
                right={wa && (
                  <Tooltip arrow title={hasJob ? "Compute today's message and show it per recipient. Sends nothing." : 'Preview is not available for this alert yet'}>
                    <span><Button size='small' variant='outlined' startIcon={<VisibilityOutlinedIcon />} onClick={onPreview} disabled={busy || !hasJob} sx={btnSx}>{previewing ? 'Computing…' : 'Preview'}</Button></span>
                  </Tooltip>
                )}>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: MUTED, mb: 0.25 }}>Last send</Typography>
                    {hasRun ? (
                      <Typography sx={{ fontSize: '0.82rem', color: INK, lineHeight: 1.45 }}>
                        {shortTime(d.lastRun.at)} IST <Box component='span' sx={{ color: MUTED }}>· {relTime(d.lastRun.at)}</Box>
                        <br /><Box component='span' sx={{ color: d.lastRun.failed ? RED : INK, fontWeight: 600 }}>{runSummary(d.lastRun)}</Box>
                        <Box component='span' sx={{ color: MUTED }}> · {count(reached.size)} of {count(active.length)} active reached</Box>
                      </Typography>
                    ) : <Typography sx={{ fontSize: '0.82rem', color: MUTED }}>No send recorded yet.</Typography>}
                  </Box>
                  {wa && <Box>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: MUTED, mb: 0.25 }}>Last Proship run</Typography>
                    {d.lastRunResult ? (
                      <Typography sx={{ fontSize: '0.82rem', color: d.lastRunResult.error ? RED : INK, lineHeight: 1.45 }}>
                        {shortTime(d.lastRunResult.startedAt)} IST <Box component='span' sx={{ color: MUTED }}>· {relTime(d.lastRunResult.startedAt)} · {d.lastRunResult.trigger}{d.lastRunResult.actor ? ` · ${d.lastRunResult.actor}` : ''}</Box>
                        <br />{d.lastRunResult.error ? d.lastRunResult.error : `${count(d.lastRunResult.messagesQueued)} queued for ${d.lastRunResult.day} (${count(d.lastRunResult.bodies)} bod${d.lastRunResult.bodies === 1 ? 'y' : 'ies'} × ${count(d.lastRunResult.recipients)} recipient${d.lastRunResult.recipients === 1 ? '' : 's'})`}
                      </Typography>
                    ) : <Typography sx={{ fontSize: '0.82rem', color: MUTED }}>{hasJob ? 'Not run by Proship yet — Preview shows what it would send today.' : '—'}</Typography>}
                  </Box>}
                </Box>
              </SCard>
            </Box>

            {/* ── Recipients ── */}
            <SCard title='Recipients' badge={`${count(active.length)} active${inactive.length ? ` · ${count(inactive.length)} inactive` : ''}`}
              sub={email ? 'To / CC / BCC as the mail header will carry them — a group mailbox is a normal recipient' : undefined}
              right={!locked && !adding && <Button size='small' variant='outlined' startIcon={<PersonAddAltOutlinedIcon />} onClick={() => setAdding(true)} disabled={busy} sx={btnSx}>Add recipient</Button>}>
              {adding && <Box sx={{ p: 2, pb: 0 }}><RecipientForm templateName={templateName} channel={channel} existingKeys={active.map(recipientAddress)} onDone={() => setAdding(false)} onCancel={() => setAdding(false)} /></Box>}
              {(d.recipients || []).length ? (
                <Table size='small' sx={{ '& td': { fontSize: '0.8rem', borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap' } }}>
                  <TableHead>
                    <TableRow>{['Name', meta.addressLabel, ...(email ? ['Header'] : []), 'Status', 'Reached in last run', 'Added', ''].map((h, i) => <Th key={i} sx={{ position: 'static' }}>{h}</Th>)}</TableRow>
                  </TableHead>
                  <TableBody>
                    {[...active, ...inactive].map(r => {
                      const address = recipientAddress(r)
                      const wasReached = reached.has(address)
                      return (
                        <TableRow key={address} hover sx={{ opacity: r.active ? 1 : 0.55 }}>
                          <TableCell sx={{ fontWeight: 700, color: INK }}>{r.displayName || <Box component='span' sx={{ color: MUTED }}>—</Box>}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{address}</TableCell>
                          {email && <TableCell><Pill tone={(r.headerType || 'TO') === 'TO' ? 'info' : 'neutral'}>{headerLabel(r.headerType)}</Pill></TableCell>}
                          <TableCell><Pill tone={r.active ? 'success' : 'neutral'}>{r.active ? 'Active' : 'Inactive'}</Pill></TableCell>
                          <TableCell>
                            {!hasRun ? <Box component='span' sx={{ color: MUTED }}>—</Box>
                              : wasReached ? <Tooltip title={`A message for this ${meta.recipientNoun} was accepted in the last run`} arrow><Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: GREEN, fontWeight: 600 }}><CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> Yes</Box></Tooltip>
                              : r.active ? <Tooltip title={locked ? 'Configured, but no message reached this number in the last run — Control Tower decides who is sent to until migration' : `Configured, but no message reached this ${meta.recipientNoun} in the last run`} arrow><Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: RED, fontWeight: 700 }}><HighlightOffOutlinedIcon sx={{ fontSize: 16 }} /> No</Box></Tooltip>
                              : <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: MUTED }}><RemoveCircleOutlineIcon sx={{ fontSize: 16 }} /> n/a</Box>}
                          </TableCell>
                          <TableCell sx={{ color: MUTED }} title={r.addedAt ? `${r.addedAt} IST` : ''}>
                            {r.addedAt ? shortTime(r.addedAt) : '—'}{r.addedBy ? ` · ${r.addedBy.replace(/^seed:.*/, 'seed')}` : ''}
                            {!r.active && r.deactivatedAt && <Typography sx={{ display: 'block', fontSize: '0.66rem', color: MUTED }}>removed {shortTime(r.deactivatedAt)}{r.deactivatedBy ? ` · ${r.deactivatedBy}` : ''}</Typography>}
                          </TableCell>
                          <TableCell align='right'>
                            {locked ? null : r.active ? (
                              <Tooltip title='Remove from this alert (kept in history as inactive)' arrow>
                                <IconButton size='small' sx={{ color: RED }} disabled={busy} onClick={() => setConfirm({ kind: 'remove', recipient: r })}><PersonRemoveOutlinedIcon fontSize='small' /></IconButton>
                              </Tooltip>
                            ) : (
                              <Button size='small' disabled={busy} onClick={() => setConfirm({ kind: 'readd', recipient: r })} sx={btnSx}>Re-add</Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              ) : !adding && <Typography sx={{ p: 2, fontSize: '0.82rem', color: MUTED }}>No recipients configured for this alert.</Typography>}
            </SCard>

            {/* ── Change log ── */}
            <SCard title='Change log' sub='who changed what, newest first'>
              <Box sx={{ p: 2 }}><ChangeLogList templateName={templateName} /></Box>
            </SCard>
          </Box>
        )}
      </DialogContent>

      <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${LINE}`, display: 'flex', justifyContent: 'flex-end', gap: 1, background: '#fff' }}>
        <Button size='small' startIcon={<HistoryOutlinedIcon />} onClick={() => onShowHistory?.(templateName)} sx={btnSx} disabled={!d}>View messages</Button>
        <Button size='small' variant='contained' onClick={onClose} sx={btnSx} disabled={busy}>Close</Button>
      </Box>

      <ConfirmDialog
        open={Boolean(confirm)} busy={busy} onCancel={() => setConfirm(null)} onConfirm={doConfirm}
        title={confirm?.kind === 'disable' ? `Switch off “${d?.displayName}”?` : confirm?.kind === 'enable' ? `Switch on “${d?.displayName}”?`
          : confirm?.kind === 'remove' ? `Remove ${confirm.recipient.displayName || recipientAddress(confirm.recipient)}?`
          : confirm?.kind === 'readd' ? `Re-add ${confirm.recipient.displayName || recipientAddress(confirm.recipient)}?` : ''}
        confirmLabel={confirm?.kind === 'disable' ? 'Switch off' : confirm?.kind === 'enable' ? 'Switch on' : confirm?.kind === 'remove' ? 'Remove' : 'Re-add'}
        tone={confirm?.kind === 'disable' || confirm?.kind === 'remove' ? 'error' : 'primary'}
        withReason={confirm?.kind !== 'readd'}
        reasonRequired={confirm?.kind === 'disable' || (confirm?.kind === 'remove' && others(confirm.recipient).length === 0)}
      >
        {confirm?.kind === 'disable' && <>Proship will skip this alert from its next run until it is switched on again. Nobody will receive it.</>}
        {confirm?.kind === 'enable' && <>Proship will resume this alert on its next run, to {count(active.length)} active recipient{active.length === 1 ? '' : 's'}.</>}
        {confirm?.kind === 'remove' && (
          others(confirm.recipient).length
            ? <>They stop receiving it from the next run. Still receiving: {others(confirm.recipient).map(a => a.displayName || recipientAddress(a)).join(', ')}.</>
            : <Box component='span' sx={{ color: RED, fontWeight: 600 }}>This is the last active recipient — the alert will run but reach nobody.</Box>
        )}
        {confirm?.kind === 'readd' && <>They will receive this alert again from the next run.</>}
      </ConfirmDialog>

      <RunResultDialog open={Boolean(runResult)} title={runResult?.title} result={runResult?.result} onClose={() => setRunResult(null)} />
    </Dialog>
  )
}
